import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Icons } from "assets";
import { routeConstant } from "shared/routes/routeConstant";
import { ResetPasswordService } from "shared/services/auth";
import { toastMessage } from "shared/components/common/toast";
import { handleErrors } from "shared/utils/helper";
import { useFormik } from "formik";
import { ResetPasswordVS } from "shared/utils/validation";
import CustomInput from "shared/components/common/customInput";
import classNames from "classnames";
import CustomButton from "shared/components/common/customButton";
import styles from "./style.module.scss";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);

  const tokenFromUrl = searchParams.get("token") || "";

  useEffect(() => {
    if (!tokenFromUrl) {
      navigate(routeConstant.login.path);
    }
  }, [tokenFromUrl, navigate]);

  const formik = useFormik({
    initialValues: {
      resetToken: tokenFromUrl,
      password: "",
      confirmPassword: "",
    },
    enableReinitialize: true,
    validationSchema: ResetPasswordVS,
    onSubmit: async (values, { setSubmitting }) => {
      setLoading(true);
      setSubmitting(true);
      try {
        const response = await ResetPasswordService({
          resetToken: values.resetToken,
          password: values.password,
          confirmPassword: values.confirmPassword,
        });

        if (response.status === 200 || response.status === 201) {
          const message =
            response.data?.message ||
            response.data?.data?.message ||
            "Password has been reset successfully";
          toastMessage("success", message);
          setTimeout(() => {
            navigate(routeConstant.login.path);
          }, 2000);
        }
      } catch (error: any) {
        handleErrors(error);
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  const { handleChange, handleSubmit, values, touched, errors } = formik;

  return (
    <section
      className={classNames(
        "w-full flex flex-col items-center relative",
        styles.leftContainer,
      )}
    >
      <div className="flex items-center w-full justify-center m-auto">
        <form
          className={styles.formContainer}
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit(e);
          }}
          noValidate
        >
          <div className="flex flex-col gap-3 text-center">
            <h1 className={classNames(styles.title, "text-center")}>
              Reset Password
            </h1>
            <p className={classNames(styles.subTitle, "text-center")}>
              Enter your new password below.
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <CustomInput
              label="Password"
              placeholder="•••••••••••"
              required
              isPassword
              Icon={Icons.Lock}
              type="password"
              value={values.password}
              error={touched.password && errors.password ? errors.password : ""}
              onChange={handleChange("password")}
              customLabelStyle={styles.labelStyle}
            />

            <CustomInput
              label="Confirm Password"
              placeholder="•••••••••••"
              required
              isPassword
              Icon={Icons.Lock}
              type="password"
              value={values.confirmPassword}
              error={
                touched.confirmPassword && errors.confirmPassword
                  ? errors.confirmPassword
                  : ""
              }
              onChange={handleChange("confirmPassword")}
              customLabelStyle={styles.labelStyle}
            />

            <div>
              <CustomButton
                title="Reset Password"
                containerStyle="w-full bg-blue maxHeighted_btn"
                type="submit"
                loading={loading}
                disabled={loading}
              />
            </div>

            <div
              className={classNames(
                styles.link_container,
                "flex gap-2 items-center justify-center cursor-pointer mt-1",
              )}
              onClick={() => navigate(routeConstant.login.path)}
            >
              <Icons.ArrowLeftCircle />
              <p>Back To Login</p>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
};

export default ResetPassword;
